(ns hai.tasks.facets
  "The kanban tasks-board filter surfaces: the assignee facet sidebar - the one
   place the board's filter is set - and the applied-filter chips shown in the
   page header. Both drive the collection's standard `:table/filters` form (the
   sub-field `set-value!` for the sidebar, the map-level field for the chips), so
   the query plumbing is untouched. Options and their counts come from
   `:user/task-facets` via [[hai.tasks.data/load-facets]].

   A task-specific fork of `hai.notes.facets`, deliberately narrowed to a single
   assignee group (the board filters on assignee alone)."
  (:require
   [hai.tasks.data :as data]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private person-icon (icons/mui-icon :outlined :person))
(def ^:private tag-icon (icons/mui-icon :outlined :label))

;; The filter attr the assignee facet writes. A `:user/uuid` selects that
;; assignee; the `:unassigned` sentinel keyword selects tasks with no assignee.
(def ^:private assigned-to-attr :task/assigned-to)

;; ---------------------------------------------------------------------------
;; Applied-filter chips (page header)
;; ---------------------------------------------------------------------------

(o/defstyled chips-row :div
  {:display "flex" :align-items "center" :flex-wrap "wrap"
   :gap "6px" :min-width "0"})

(o/defstyled filter-chip :span
  {:display "inline-flex" :align-items "center" :gap "6px"
   :height "26px" :padding "0 4px 0 10px"
   :border-radius "999px"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-14
   :background t/--colour-snow
   :font-size "12px" :color t/--card-forest
   :white-space "nowrap"}
  [:b {:font-weight "600"}]
  [:.lead-icon {:display "inline-flex"}
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
  [:.remove
   {:display "inline-flex" :align-items "center" :justify-content "center"
    :width "18px" :height "18px" :border "none" :border-radius "999px"
    :background "transparent" :padding "0"
    :color t/--card-forest :cursor "pointer"}
   [:&:hover {:background t/--card-forest-10}]
   [:svg {:width "11px" :height "11px" :fill "currentColor"}]])

(defn applied-chips
  "The header's row of currently-applied filters - one chip per filter column
   holding a value, showing the column's pill face (`:label`/`:icon`/`:summary`)
   with an × that resets it. Clearing sets the field on the filters SUB-form
   (id `[<root-form-id> :table/filters]`) - the same path the sidebar writes -
   because sub-form values propagate up to the root form but a root-level write
   never reaches the sub-form's fields, which would leave the sidebar's
   selection highlight stale. Renders nothing when no filter is applied."
  [table]
  (let [columns (<s [::filters/columns table])
        filters-form-id [(-> table :table/form :form/id) :table/filters]
        {:form.field/keys [current-value]}
        (get-in table [:table/form :form/fields :table/filters])
        applied (for [{:table.column/keys [attr filter] :as column} @columns
                      :let [{:table.column.filter/keys [label icon summary]} filter
                            value (get current-value attr)]
                      :when (and label (some? value))]
                  ^{:key attr}
                  [filter-chip
                   (when icon [:span.lead-icon [icon]])
                   [:b (str label ":")]
                   [:span (when summary (summary value column))]
                   [:button.remove
                    {:type "button"
                     :aria-label (str "Reset " label " filter")
                     :on-click (fn []
                                 (>e [:form.field/set-value! filters-form-id attr nil])
                                 (>e [::data/load-facets]))}
                    [icons/outlined-close]]])]
    (when (seq applied)
      (into [chips-row] applied))))

;; ---------------------------------------------------------------------------
;; Facet sidebar
;; ---------------------------------------------------------------------------

;; Garden keyframes are evaluated at macro time, so garden.stylesheet needs no
;; cljs require here.
#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules skeleton-pulse
  (garden.stylesheet/at-keyframes
   :hai-facet-skeleton-pulse
   [:from {:opacity "1"}]
   ["50%" {:opacity "0.7"}]
   [:to {:opacity "1"}]))

(o/defstyled sidebar :aside
  {:width "240px"
   :position "sticky" :top "16px"
   :align-self "start"
   :max-height "calc(100vh - 120px)"
   :overflow-y "auto"
   :display "flex" :flex-direction "column" :gap "16px"
   ;; No padding-top: the sticky `.facet-heading` uses `top: 0`, which pins to
   ;; the scroll container's PADDING edge. Any padding-top would leave a band
   ;; above the pinned heading where a scrolling option shows through.
   :padding "0 12px 14px"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-14
   :border-radius "10px"
   :background t/--colour-snow
   :scrollbar-width "thin"
   :scrollbar-color "rgba(45,62,42,0.4) transparent"}
  [:.facet-group {:display "flex" :flex-direction "column" :gap "6px"}]
  [:.facet-heading
   ;; Sticky within its own `.facet-group`: the heading stays pinned to the top
   ;; of the scroll area until the group's last option scrolls past, then the
   ;; next group's heading pushes it out. Needs an opaque background and a
   ;; z-index so options scroll UNDER it.
   {:position "sticky" :top "0" :z-index "1"
    :display "flex" :align-items "center" :gap "6px"
    :padding "4px 0 5px"
    :background t/--colour-snow
    :font-size "11px" :font-weight "700"
    :letter-spacing "0.06em" :text-transform "uppercase"
    :color t/--card-forest-70}
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
  [:.facet-options {:display "flex" :flex-direction "column" :gap "2px"}]
  ;; Placeholder bar shown while the first facet load is in flight, so an
  ;; unloaded sidebar reads as "loading" rather than "no filters".
  [:.facet-skeleton
   {:height "13px" :border-radius "7px"
    :background t/--card-forest-10
    :animation "hai-facet-skeleton-pulse 1.4s ease-in-out infinite"}]
  [:.facet-option
   {:display "flex" :align-items "center" :gap "8px"
    :width "100%" :text-align "left"
    :padding "5px 8px" :border "none" :border-radius "7px"
    :background "transparent"
    :font-family "inherit" :font-size "13px" :font-weight "500"
    :color t/--card-forest :cursor "pointer"
    :min-width "0"}
   [:&:hover {:background t/--card-forest-10}]
   [:&.selected {:background t/--card-forest-10 :font-weight "700"}]
   [:&.zero {:opacity "0.45"}]
   [:.opt-label
    {:flex "1" :min-width "0"
     :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}]
   [:.opt-count {:flex "0 0 auto" :font-size "12px" :font-weight "500"
                 :color t/--card-forest-70}]]
  [:.clear-all
   {:align-self "flex-start"
    :border "none" :background "transparent" :padding "0 8px"
    :font-family "inherit" :font-size "12px" :font-weight "600"
    :color t/--card-forest-70 :cursor "pointer"}
   [:&:hover {:color t/--card-forest :text-decoration "underline"}]]
  [:at-media {:max-width "768px"}
   {:position "static" :width "100%" :max-height "none"}])

(defn- facet-option
  [{:keys [label amount selected? on-toggle title]}]
  [:button.facet-option
   {:type     "button"
    :class    (cond-> []
                selected? (conj "selected")
                ;; Nothing matches this option under the current constraints.
                (and (some? amount) (zero? amount) (not selected?)) (conj "zero"))
    :title    (or title label)
    :on-click on-toggle}
   [:span.opt-label label]
   (when (some? amount) [:span.opt-count amount])])

(defn- toggle-value!
  "Single-select facet click: picking the selected option again clears it.
   The counts re-query after - the set event is processed first, so the
   reload sees the new value."
  [set-value! current-value value]
  (set-value! (if (= current-value value) nil value))
  (>e [::data/load-facets]))

(defn- field-state [table attr]
  (get-in table [:table/form :form/fields attr]))

(defn- clear-all!
  [table]
  (doseq [[_ {:form.field/keys [set-value! current-value]}]
          (-> table :table/form :form/fields)]
    (when (and set-value! (some? current-value))
      (set-value! nil)))
  (>e [::data/load-facets]))

(defn- tags-group
  "The tag facet group: an Untagged sentinel row over every tag on the board's
   open tasks, each with its smart count. Single-select against the `:task/tags`
   filter field. The value for a tag is a one-element name vector `[tag-name]`
   (the name-based filter the server reuses from notes); the sentinel is the
   `:untagged` keyword. Mirrors the notes wall's `tags-group`."
  [table tags untagged-count]
  (let [{:form.field/keys [current-value set-value!]} (field-state table :task/tags)]
    [:section.facet-group
     [:div.facet-heading [tag-icon] "Tag"]
     (into [:div.facet-options
            [facet-option {:label     "Untagged"
                           :amount    untagged-count
                           :selected? (= current-value :untagged)
                           :on-toggle #(toggle-value! set-value! current-value :untagged)}]]
           (for [{tag-name :tag/name amount :facet/count} tags
                 :let [value [tag-name]]]
             ^{:key tag-name}
             [facet-option {:label     (str "#" tag-name)
                            :amount    amount
                            :selected? (= current-value value)
                            :on-toggle #(toggle-value! set-value! current-value value)}]))]))

(defn- assignees-group
  "The single assignee facet group: an Unassigned sentinel row over every
   distinct assignee in the board's open tasks, each with its smart count.
   Single-select against the `:task/assigned-to` filter field."
  [table assignees unassigned-count]
  (let [{:form.field/keys [current-value set-value!]} (field-state table assigned-to-attr)]
    [:section.facet-group
     [:div.facet-heading [person-icon] "Assignee"]
     (into [:div.facet-options
            [facet-option {:label     "Unassigned"
                           :amount    unassigned-count
                           :selected? (= current-value :unassigned)
                           :on-toggle #(toggle-value! set-value! current-value :unassigned)}]]
           (for [{uuid :user/uuid full-name :user/full-name amount :facet/count} assignees]
             ^{:key uuid}
             [facet-option {:label     full-name
                            :amount    amount
                            :selected? (= current-value uuid)
                            :on-toggle #(toggle-value! set-value! current-value uuid)}]))]))

(defn- skeleton-group
  "A section heading over a single pulsing placeholder bar, shown per dimension
   while the first facet load is pending."
  [icon heading width]
  [:section.facet-group
   [:div.facet-heading [icon] heading]
   [:div.facet-options
    [:div.facet-skeleton {:style {:width width}}]]])

(defn- sidebar-panel [table]
  (r/with-let [_ (>e [::data/load-facets])]
    (let [facets @(<s [::data/facets])
          ;; nil until the first `::load-facets` lands; a loaded-but-empty board
          ;; comes back as a (non-nil) map, so this cleanly tells the two apart.
          loading? (nil? facets)
          {:facet/keys [tags untagged-count assignees unassigned-count]} facets
          any-applied? (some (fn [[_ {:form.field/keys [current-value]}]]
                               (some? current-value))
                             (-> table :table/form :form/fields))]
      (if loading?
        [sidebar
         [skeleton-group tag-icon "Tag" "78%"]
         [skeleton-group person-icon "Assignee" "72%"]]
        [sidebar
         [tags-group table tags untagged-count]
         [assignees-group table assignees unassigned-count]
         (when any-applied?
           [:button.clear-all
            {:type "button" :on-click #(clear-all! table)}
            "Clear all filters"])]))))

(defn panel
  "`:filters/render` for the tasks board: the assignee facet sidebar. The kanban
   view mounts it as a sibling of the lane grid (via `:filters/placement
   :sibling`); the page grid places it in the left column."
  [table]
  [sidebar-panel table])
